import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { LuX } from 'react-icons/lu'

// Clases compartidas por los formularios de los modales
export const inputCls = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand-soft disabled:bg-gray-50 disabled:text-gray-500'
export const btnPrimary = 'flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50'
export const btnSecondary = 'flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50'
export const btnGhost = 'flex h-10 items-center rounded-lg px-4 text-sm font-semibold text-gray-600 hover:bg-gray-100'
export const btnDanger = 'flex h-10 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50'

// Modal centrado: cierra con Escape o clicando fuera
export default function Dialog({ title, subtitle, onClose, size, children }) {
  const { t } = useTranslation('common')

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const width = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }[size] || 'max-w-lg'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-[2px]"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div role="dialog" aria-modal="true" className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl ${width}`}>
        {title && (
          <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-gray-900">{title}</h2>
              {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
            </div>
            <button type="button" onClick={onClose} aria-label={t('close', { defaultValue: 'Cerrar' })}
              className="-mr-2 flex h-8 w-8 flex-none items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
              <LuX size={18} />
            </button>
          </div>
        )}
        <div className="overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  )
}

Dialog.propTypes = {
  title:    PropTypes.node,
  subtitle: PropTypes.node,
  onClose:  PropTypes.func.isRequired,
  size:     PropTypes.oneOf(['sm', 'md', 'lg']),
  children: PropTypes.node,
}
Dialog.defaultProps = { title: null, subtitle: null, size: 'md', children: null }

export function Field({ label, hint, className, children }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {children}
      {hint && <span className="text-xs text-gray-500">{hint}</span>}
    </label>
  )
}

Field.propTypes = { label: PropTypes.node, hint: PropTypes.node, className: PropTypes.string, children: PropTypes.node }
Field.defaultProps = { label: null, hint: null, className: '', children: null }
